import { Settings } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'

interface HeaderProps {
  title?: string
  showActions?: boolean
  showBack?: boolean
  onBack?: () => void
  rightExtra?: React.ReactNode
  onOpenToss?: () => void
}

export function CricketHeader({
  title = 'Cricket scorer',
  showActions = true,
  showBack = false,
  onBack,
  rightExtra,
  onOpenToss,
}: HeaderProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
  const [isInstallable, setIsInstallable] = useState(false)

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setIsInstallable(true)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setIsInstallable(false)
    }
    setDeferredPrompt(null)
  }

  return (
    <header className="bg-[var(--color-cricket-primary,#2E7D32)] text-white px-4 py-3 flex items-center justify-between sticky top-0 z-40 shadow-sm transition-colors">
      <div className="flex items-center gap-3">
        {showBack && (
          <button
            onClick={onBack || (() => window.history.back())}
            className="p-1 hover:bg-white/10 rounded-full transition-colors active:scale-90"
            aria-label="Back"
          >
            <svg
              className="size-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
        )}
        <h1 className="text-xl font-bold tracking-tight text-white flex items-center gap-2">
          {title}
        </h1>
      </div>

      <div className="flex items-center gap-2">
        {isInstallable && (
          <button
            onClick={handleInstallClick}
            className="text-xs bg-white text-[#2E7D32] font-semibold px-2.5 py-1 rounded-full shadow-sm hover:bg-white/90 active:scale-95 transition-all"
          >
            Install App
          </button>
        )}

        {rightExtra}

        {onOpenToss && (
          <button
            type="button"
            onClick={onOpenToss}
            className="p-1.5 hover:bg-white/10 rounded-full transition-colors active:scale-90 text-white flex items-center justify-center"
            title="3D Coin Toss"
            aria-label="3D Coin Toss"
          >
            <span className="text-lg leading-none">🪙</span>
          </button>
        )}

        {showActions && (
          <Link
            to="/settings"
            className="p-1.5 hover:bg-white/10 rounded-full transition-colors active:scale-90 text-white"
            aria-label="Settings"
          >
            <Settings className="size-5.5" />
          </Link>
        )}
      </div>
    </header>
  )
}
